import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listMyTicketApprovals, listNotifications, markAllNotificationsRead, markNotificationRead } from "../lib/api";
import { ApiError } from "../lib/api";
import { getCurrentUser } from "../lib/auth";
import { timeAgo } from "../lib/format";
import type {
  NotificationRow,
  PendingChangeApproval,
  PendingKbApproval,
  PendingTaskApproval,
  PendingTicketApproval,
} from "../lib/types";

const POLL_MS = 30_000;

/** Top-bar notifications bell: polls the in-app feed, shows the unread count,
 *  and a dropdown to read / mark-all-read individual notifications. */
export function NotificationsBell() {
  const navigate = useNavigate();
  const userId = getCurrentUser()?.id ?? "";

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);
  // Approvals awaiting my decision (ticket + task) — counted in the badge too.
  const [ticketApprovals, setTicketApprovals] = useState<PendingTicketApproval[]>([]);
  const [taskApprovals, setTaskApprovals] = useState<PendingTaskApproval[]>([]);
  const [kbApprovals, setKbApprovals] = useState<PendingKbApproval[]>([]);
  const [changeApprovals, setChangeApprovals] = useState<PendingChangeApproval[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pendingApprovals =
    ticketApprovals.length + taskApprovals.length + kbApprovals.length + changeApprovals.length;
  const badge = unread + pendingApprovals;

  const refresh = useCallback(
    (signal?: AbortSignal) => {
      if (!userId) return;
      listNotifications(signal)
        .then((res) => {
          if (signal?.aborted) return;
          setItems(res.notifications);
          setUnread(res.unread);
        })
        .catch((err: unknown) => {
          // A 401 is handled app-wide elsewhere; ignore polling errors quietly.
          if (err instanceof ApiError && err.status === 401) return;
        });
      listMyTicketApprovals(signal)
        .then((res) => {
          if (signal?.aborted) return;
          setTicketApprovals(res.approvals ?? []);
          setTaskApprovals(res.taskApprovals ?? []);
          setKbApprovals(res.kbApprovals ?? []);
          setChangeApprovals(res.changeApprovals ?? []);
        })
        .catch(() => {
          /* approvals are best-effort in the bell */
        });
    },
    [userId],
  );

  // Poll on mount + on an interval. Refetch immediately when the menu opens.
  useEffect(() => {
    if (!userId) return;
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    const t = window.setInterval(() => refresh(), POLL_MS);
    return () => {
      ctrl.abort();
      window.clearInterval(t);
    };
  }, [userId, refresh]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) refresh();
  }

  async function openNotification(n: NotificationRow) {
    setOpen(false);
    if (!n.read_at) {
      // Optimistic local update, then persist.
      setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read_at: Math.floor(Date.now() / 1000) } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try {
        await markNotificationRead(n.id);
      } catch {
        /* keep the optimistic state; a later poll reconciles */
      }
    }
    // Links from the worker are app-relative paths (e.g. "/tickets/:id").
    if (n.link && n.link.startsWith("/")) navigate(n.link);
  }

  async function markRead(n: NotificationRow, e: React.MouseEvent) {
    e.stopPropagation();
    if (n.read_at) return;
    setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read_at: Math.floor(Date.now() / 1000) } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await markNotificationRead(n.id);
    } catch {
      /* reconcile on next poll */
    }
  }

  async function markAll() {
    if (!userId || unread === 0) return;
    const now = Math.floor(Date.now() / 1000);
    setItems((xs) => xs.map((x) => (x.read_at ? x : { ...x, read_at: now })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      /* reconcile on next poll */
    }
  }

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        className="iconbtn"
        type="button"
        aria-label={`Notifications${badge ? ` (${badge} unseen)` : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={toggle}
      >
        🔔
        {badge > 0 ? (
          <span className="notif-count" aria-hidden="true">
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="notif-menu" role="menu" aria-label="Notifications">
          <div className="notif-menu-head">
            <strong>Notifications</strong>
            <button className="btn ghost small" onClick={markAll} disabled={unread === 0}>
              Mark all read
            </button>
          </div>
          {pendingApprovals > 0 ? (
            <div className="notif-approvals">
              <div className="notif-section-title faint">Awaiting your approval</div>
              {ticketApprovals.map((a) => (
                // Same structure as a regular notification row (wrapper + inner
                // open-target + unread dot) so approvals render identically.
                <div key={a.id} className="notif-item unread">
                  <Link
                    to={`/tickets/${a.ticket_id}`}
                    className="notif-item-open"
                    onClick={() => setOpen(false)}
                  >
                    <div className="notif-title">
                      Ticket #{a.ticket_number ?? "?"}: {a.ticket_subject}
                    </div>
                    <div className="faint notif-time">
                      {a.requested_by_name ? `from ${a.requested_by_name} · ` : ""}
                      {timeAgo(a.created_at)}
                    </div>
                  </Link>
                  <span className="notif-dot" aria-hidden="true">
                    ●
                  </span>
                </div>
              ))}
              {taskApprovals.map((a) => (
                <div key={a.id} className="notif-item unread">
                  <Link
                    to={`/action-items?open=${a.task_id}`}
                    className="notif-item-open"
                    onClick={() => setOpen(false)}
                  >
                    <div className="notif-title">Action item: {a.task_title}</div>
                    <div className="faint notif-time">
                      {a.requested_by_name ? `from ${a.requested_by_name} · ` : ""}
                      {timeAgo(a.created_at)}
                    </div>
                  </Link>
                  <span className="notif-dot" aria-hidden="true">
                    ●
                  </span>
                </div>
              ))}
              {kbApprovals.map((a) => (
                <div key={a.id} className="notif-item unread">
                  <Link
                    to={`/kb?open=${a.article_id}`}
                    className="notif-item-open"
                    onClick={() => setOpen(false)}
                  >
                    <div className="notif-title">KB article: {a.article_title}</div>
                    <div className="faint notif-time">
                      {a.requested_by_name ? `from ${a.requested_by_name} · ` : ""}
                      {timeAgo(a.created_at)}
                    </div>
                  </Link>
                  <span className="notif-dot" aria-hidden="true">
                    ●
                  </span>
                </div>
              ))}
              {changeApprovals.map((a) => (
                <div key={a.id} className="notif-item unread">
                  <Link
                    to={`/change-management/${a.request_id}`}
                    className="notif-item-open"
                    onClick={() => setOpen(false)}
                  >
                    <div className="notif-title">
                      Change #{a.number}: {a.title}
                    </div>
                    <div className="faint notif-time">
                      {a.requested_by_name ? `from ${a.requested_by_name} · ` : ""}
                      {timeAgo(a.created_at)}
                    </div>
                  </Link>
                  <span className="notif-dot" aria-hidden="true">
                    ●
                  </span>
                </div>
              ))}
            </div>
          ) : null}
          <div className="notif-list">
            {items.length === 0 ? (
              // Only truly "caught up" when there are no pending approvals either —
              // otherwise the approval section above contradicts this message.
              pendingApprovals === 0 ? (
                <div className="notif-empty muted">You're all caught up.</div>
              ) : null
            ) : (
              items.map((n) => (
                // Row is a plain container holding two REAL sibling buttons — a
                // button must never nest another interactive control (invalid +
                // keyboard-unreachable).
                <div key={n.id} className={`notif-item${n.read_at ? "" : " unread"}`}>
                  <button
                    type="button"
                    role="menuitem"
                    className="notif-item-open"
                    onClick={() => openNotification(n)}
                  >
                    <div className="notif-title">{n.title}</div>
                    {n.body ? <div className="notif-body muted">{n.body}</div> : null}
                    <div className="faint notif-time">{timeAgo(n.created_at)}</div>
                  </button>
                  {!n.read_at ? (
                    <button
                      type="button"
                      className="notif-mark"
                      title="Mark read"
                      aria-label="Mark read"
                      onClick={(e) => markRead(n, e)}
                    >
                      ●
                    </button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
